import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { playGsIncreaseSound, playGsKpCombinedSound, playGsRewardSound, playKpIncreaseSound } from './gamificationAudio';

type FlyPointOptions = {
  fromEl?: HTMLElement;
  delta: number;
  holdMs?: number;
  nextValue?: number;
  combined?: boolean;
  xOffset?: number;
};
export type FlyGsOptions = FlyPointOptions;
export type FlyKpOptions = FlyPointOptions;

const GS_ICON_ANIMATION_MS = 1200;
const GS_LABEL_WIPE_MS = 400;
const GS_DIGIT_CHANGE_DELAY_MS = 40;
const GS_LABEL_HOLD_MS = 400;
const KP_BULB_FILL_MS = 1300;
const KP_BULB_BLINK_MS = 900;
const KP_LABEL_WIPE_MS = 400;
const KP_DIGIT_CHANGE_DELAY_MS = 40;
const KP_LABEL_HOLD_MS = 400;

// --- Effect bus factory (Item 3) ---

function makeEffectBus<T extends (value: number) => void>() {
  let handler: T | null = null;

  const trigger = (value: number) => { handler?.(value); };

  const register = (fn: T) => {
    handler = fn;
    return () => { if (handler === fn) handler = null; };
  };

  return { trigger, register };
}

const gsBus = makeEffectBus<(nextGs: number) => void>();
const kpBus = makeEffectBus<(nextKp: number) => void>();

const triggerGsHeaderEffects = gsBus.trigger;
const registerGsHeaderEffects = gsBus.register;
const triggerKpHeaderEffects = kpBus.trigger;
const registerKpHeaderEffects = kpBus.register;

// --- Header effects hook (Item 4) ---

type GamificationHeaderEffects = {
  gsIconRef: RefObject<SVGSVGElement>;
  displayedGs: number;
  displayedKp: number;
  isGsLabelWiping: boolean;
  isGsLabelHighlighted: boolean;
  isKpLabelWiping: boolean;
  isKpLabelHighlighted: boolean;
  isKpBulbFilling: boolean;
  isKpBulbBlinking: boolean;
  isKpBulbLit: boolean;
};

function clearScheduled(timeoutsRef: React.RefObject<number[]>, animatingRef: React.RefObject<boolean>) {
  timeoutsRef.current.forEach((id) => window.clearTimeout(id));
  timeoutsRef.current = [];
  animatingRef.current = false;
}

function scheduleStep(timeoutsRef: React.RefObject<number[]>, delayMs: number, fn: () => void) {
  timeoutsRef.current.push(window.setTimeout(fn, delayMs));
}

export const useGamificationHeaderEffects = ({ kp, gs }: { kp: number; gs: number }): GamificationHeaderEffects => {
  const gsIconRef = useRef<SVGSVGElement | null>(null);
  const gsTimeoutsRef = useRef<number[]>([]);
  const kpTimeoutsRef = useRef<number[]>([]);
  const [displayedGs, setDisplayedGs] = useState(gs);
  const [displayedKp, setDisplayedKp] = useState(kp);
  const [isGsLabelWiping, setIsGsLabelWiping] = useState(false);
  const [isGsLabelHighlighted, setIsGsLabelHighlighted] = useState(false);
  const [isKpLabelWiping, setIsKpLabelWiping] = useState(false);
  const [isKpLabelHighlighted, setIsKpLabelHighlighted] = useState(false);
  const [isKpBulbFilling, setIsKpBulbFilling] = useState(false);
  const [isKpBulbBlinking, setIsKpBulbBlinking] = useState(false);
  const [isKpBulbLit, setIsKpBulbLit] = useState(false);
  const isGsAnimatingRef = useRef(false);
  const displayedGsRef = useRef(displayedGs);
  const isKpAnimatingRef = useRef(false);
  const displayedKpRef = useRef(displayedKp);
  const lastTriggeredKpRef = useRef<number | null>(null);

  useEffect(() => { displayedGsRef.current = displayedGs; }, [displayedGs]);
  useEffect(() => { displayedKpRef.current = displayedKp; }, [displayedKp]);

  const clearGs = useCallback(() => clearScheduled(gsTimeoutsRef, isGsAnimatingRef), []);
  const clearKp = useCallback(() => clearScheduled(kpTimeoutsRef, isKpAnimatingRef), []);

  const runGsEffects = useCallback(
    (nextGs: number) => {
      if (!Number.isFinite(nextGs)) return;
      if (nextGs <= displayedGsRef.current) {
        setDisplayedGs(nextGs);
        return;
      }

      clearGs();
      setIsGsLabelWiping(false);
      setIsGsLabelHighlighted(false);

      const el = gsIconRef.current;
      if (!el) {
        setDisplayedGs(nextGs);
        return;
      }

      el.classList.remove('animate-gs-header-pulse-3');
      el.classList.remove('animate-gs-header-blink');
      el.style.opacity = '1';
      void el.getBoundingClientRect();
      el.classList.add('animate-gs-header-pulse-3');
      const iconEl = el;
      isGsAnimatingRef.current = true;

      const t0 = GS_ICON_ANIMATION_MS;
      const t1 = t0 + GS_LABEL_WIPE_MS;
      const t2 = t1 + GS_DIGIT_CHANGE_DELAY_MS;
      const t3 = t2 + GS_LABEL_HOLD_MS;

      scheduleStep(gsTimeoutsRef, t0, () => {
        iconEl.classList.remove('animate-gs-header-pulse-3');
        iconEl.classList.add('animate-gs-header-blink');
        setIsGsLabelWiping(true);
      });
      scheduleStep(gsTimeoutsRef, t1, () => {
        setIsGsLabelWiping(false);
        setIsGsLabelHighlighted(true);
      });
      scheduleStep(gsTimeoutsRef, t2, () => { setDisplayedGs(nextGs); });
      scheduleStep(gsTimeoutsRef, t3, () => {
        setIsGsLabelHighlighted(false);
        iconEl.classList.remove('animate-gs-header-blink');
        isGsAnimatingRef.current = false;
      });
    },
    [clearGs]
  );

  useEffect(() => {
    const unregister = registerGsHeaderEffects(runGsEffects);
    return () => { unregister(); clearGs(); };
  }, [runGsEffects, clearGs]);

  useEffect(() => {
    if (isGsAnimatingRef.current) return;
    setIsGsLabelWiping(false);
    setIsGsLabelHighlighted(false);
    if (displayedGsRef.current !== gs) setDisplayedGs(gs);
    const el = gsIconRef.current;
    if (el) {
      el.classList.remove('animate-gs-header-pulse-3');
      el.classList.remove('animate-gs-header-blink');
      el.style.opacity = '1';
    }
  }, [gs]);

  const runKpEffects = useCallback(
    (nextKp: number) => {
      if (!Number.isFinite(nextKp)) return;
      lastTriggeredKpRef.current = nextKp;

      if (nextKp <= displayedKpRef.current) {
        clearKp();
        setIsKpLabelWiping(false);
        setIsKpLabelHighlighted(false);
        setDisplayedKp(nextKp);
        setIsKpBulbFilling(false);
        setIsKpBulbBlinking(false);
        setIsKpBulbLit(false);
        return;
      }

      clearKp();
      setIsKpLabelWiping(false);
      setIsKpLabelHighlighted(false);
      setIsKpBulbFilling(true);
      setIsKpBulbBlinking(false);
      setIsKpBulbLit(false);
      isKpAnimatingRef.current = true;

      const t0 = KP_BULB_FILL_MS;
      const tWipe = t0 + KP_BULB_BLINK_MS / 2;
      const t1 = t0 + KP_BULB_BLINK_MS;
      const t2 = t1 + KP_LABEL_WIPE_MS;
      const t3 = t2 + KP_DIGIT_CHANGE_DELAY_MS;
      const t4 = t3 + KP_LABEL_HOLD_MS;

      scheduleStep(kpTimeoutsRef, t0, () => {
        setIsKpBulbFilling(false);
        setIsKpBulbBlinking(true);
        setIsKpBulbLit(true);
      });
      scheduleStep(kpTimeoutsRef, t1, () => {
        setIsKpBulbBlinking(false);
        setIsKpBulbLit(false);
      });
      scheduleStep(kpTimeoutsRef, tWipe, () => { setIsKpLabelWiping(true); });
      scheduleStep(kpTimeoutsRef, t2, () => {
        setIsKpLabelWiping(false);
        setIsKpLabelHighlighted(true);
      });
      scheduleStep(kpTimeoutsRef, t3, () => { setDisplayedKp(nextKp); });
      scheduleStep(kpTimeoutsRef, t4, () => {
        setIsKpLabelHighlighted(false);
        isKpAnimatingRef.current = false;
      });
    },
    [clearKp]
  );

  useEffect(() => {
    const unregister = registerKpHeaderEffects(runKpEffects);
    return () => { unregister(); clearKp(); };
  }, [runKpEffects, clearKp]);

  useEffect(() => {
    if (isKpAnimatingRef.current) return;
    setIsKpLabelWiping(false);
    setIsKpLabelHighlighted(false);
    setIsKpBulbFilling(false);
    setIsKpBulbBlinking(false);
    setIsKpBulbLit(false);
    if (displayedKpRef.current !== kp) setDisplayedKp(kp);
  }, [kp]);

  return {
    gsIconRef,
    displayedGs,
    displayedKp,
    isGsLabelWiping,
    isGsLabelHighlighted,
    isKpLabelWiping,
    isKpLabelHighlighted,
    isKpBulbFilling,
    isKpBulbBlinking,
    isKpBulbLit,
  };
};

// --- Fly animation (Item 1) ---

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

type FlyPointConfig = {
  anchorAttr: string;
  containerClass: string;
  labelHtml: string;
  fadeMs: number;
  computeArriveMs: (riseMs: number, holdMs: number, travelMs: number, settleMs: number) => number;
  onBeforeAnimate: (combined: boolean) => void;
  onArrive: (targetValue: number, combined: boolean) => void;
  resolveTarget: (delta: number) => number;
  extraTimeoutIds?: number[];
};

function flyPointToHeader(opts: FlyPointOptions, config: FlyPointConfig): Promise<void> {
  const { fromEl, delta, holdMs = 1500, nextValue, combined, xOffset } = opts;

  if (typeof window === 'undefined' || typeof document === 'undefined') return Promise.resolve();

  const anchorEl = document.querySelector(`[${config.anchorAttr}="true"]`) as HTMLElement | null;
  if (!anchorEl) return Promise.resolve();

  const fromRect = (fromEl ?? document.body).getBoundingClientRect();
  const toRect = anchorEl.getBoundingClientRect();

  const fromX = fromRect.left + fromRect.width / 2 + (xOffset ?? 0);
  const fromY = fromRect.top + fromRect.height / 2;
  const toX = toRect.left + toRect.width / 2;
  const toY = toRect.top + toRect.height / 2;

  const dx = toX - fromX;
  const dy = toY - fromY;
  const distance = Math.hypot(dx, dy);
  const riseMs = 480;
  const travelMs = clamp(Math.round(distance * 1.15), 800, 1800);
  const settleMs = 200;
  const { fadeMs } = config;
  const totalMs = riseMs + holdMs + travelMs + settleMs + fadeMs;
  const riseEnd = riseMs / totalMs;
  const holdEnd = (riseMs + holdMs) / totalMs;
  const travelEnd = (riseMs + holdMs + travelMs) / totalMs;
  const settleEnd = (riseMs + holdMs + travelMs + settleMs) / totalMs;
  const arriveMs = config.computeArriveMs(riseMs, holdMs, travelMs, settleMs);

  const container = document.createElement('div');
  container.className = config.containerClass;
  container.style.left = `${fromX}px`;
  container.style.top = `${fromY}px`;
  container.innerHTML = config.labelHtml;

  document.body.appendChild(container);

  const arcHeight = clamp(distance * 0.28, 70, 160);
  const arcSide = clamp(Math.abs(dx) * 0.2, 28, 90) * Math.sign(dx || 1);
  const arcX = dx * 0.45 + arcSide;
  const arcY = dy * 0.45 - arcHeight;

  if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
    container.remove();
    return Promise.resolve();
  }

  config.onBeforeAnimate(combined ?? false);

  const targetValue = typeof nextValue === 'number' ? nextValue : config.resolveTarget(delta);

  const animation = container.animate(
    [
      { transform: 'translate(-50%, -50%) translate3d(0, 20px, 0) scale(0.65) rotate(-4deg)', opacity: 0 },
      { offset: riseEnd, transform: 'translate(-50%, -50%) translate3d(0, -56px, 0) scale(1.12) rotate(-2deg)', opacity: 1 },
      { offset: holdEnd, transform: 'translate(-50%, -50%) translate3d(0, -56px, 0) scale(1.12) rotate(-2deg)', opacity: 1 },
      { offset: (holdEnd + travelEnd) / 2, transform: `translate(-50%, -50%) translate3d(${arcX}px, ${arcY}px, 0) scale(1.06) rotate(6deg)`, opacity: 1 },
      { offset: travelEnd, transform: `translate(-50%, -50%) translate3d(${dx}px, ${dy}px, 0) scale(0.98) rotate(2deg)`, opacity: 1 },
      { offset: settleEnd, transform: `translate(-50%, -50%) translate3d(${dx}px, ${dy}px, 0) scale(0.9) rotate(0deg)`, opacity: 1 },
      { transform: `translate(-50%, -50%) translate3d(${dx}px, ${dy}px, 0) scale(0.84) rotate(0deg)`, opacity: 0 },
    ],
    { duration: totalMs, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
  );

  return new Promise<void>((resolve) => {
    let hasArrived = false;
    const arriveTimeoutId = window.setTimeout(() => {
      if (Number.isFinite(targetValue)) config.onArrive(targetValue, combined ?? false);
      hasArrived = true;
      resolve();
    }, arriveMs);

    const extraIds = config.extraTimeoutIds ?? [];
    const cleanup = () => {
      window.clearTimeout(arriveTimeoutId);
      extraIds.forEach((id) => window.clearTimeout(id));
      container.remove();
      if (!hasArrived) resolve();
    };

    animation.addEventListener('finish', cleanup, { once: true });
    animation.addEventListener('cancel', cleanup, { once: true });
  });
}

function resolveGsTarget(delta: number): number {
  const valueEl = document.querySelector('[data-gs-value]') as HTMLElement | null;
  const rawValue = valueEl?.dataset.gsValue ?? valueEl?.textContent ?? '';
  const currentValue = Number(rawValue);
  return Number.isFinite(currentValue) ? currentValue + delta : NaN;
}

function resolveKpTarget(delta: number): number {
  const valueEl = document.querySelector('[data-kp-value]') as HTMLElement | null;
  const rawValue = valueEl?.dataset.kpValue ?? valueEl?.textContent ?? '';
  const currentValue = Number(rawValue);
  return Number.isFinite(currentValue) ? currentValue + delta : NaN;
}

export const flyGsToHeader = (opts: FlyGsOptions) =>
  flyPointToHeader(opts, {
    anchorAttr: 'data-gs-anchor',
    containerClass: 'gs-flyer font-zcool-kuaile',
    labelHtml: `<div class="gs-flyer-text"><span class="gs-flyer-label">GS</span><span class="gs-flyer-points">+${opts.delta}</span></div>`,
    fadeMs: 260,
    computeArriveMs: (rise, hold, travel, _settle) => rise + hold + travel - 260,
    onBeforeAnimate: () => { playGsRewardSound(); },
    onArrive: (targetValue, combined) => {
      if (combined) { playGsKpCombinedSound(); } else { playGsIncreaseSound(); }
      triggerGsHeaderEffects(targetValue);
    },
    resolveTarget: resolveGsTarget,
  });

export const flyKpToHeader = (opts: FlyKpOptions) => {
  const audioMs = (riseMs: number, holdMs: number, travelMs: number) => riseMs + holdMs + travelMs;
  const extraTimeoutIds: number[] = [];

  return flyPointToHeader(opts, {
    anchorAttr: 'data-kp-anchor',
    containerClass: 'gs-flyer kp-flyer font-zcool-kuaile',
    labelHtml: `<div class="gs-flyer-text"><span class="kp-flyer-label">KP</span><span class="kp-flyer-points">+${opts.delta}</span></div>`,
    fadeMs: 500,
    computeArriveMs: (rise, hold, travel, settle) => rise + hold + travel + settle,
    onBeforeAnimate: (combined) => {
      if (!combined) playGsRewardSound();

      // Schedule audio at travel-end (before settle)
      const fromRect = (opts.fromEl ?? document.body).getBoundingClientRect();
      const anchorEl = document.querySelector('[data-kp-anchor="true"]') as HTMLElement | null;
      if (!anchorEl) return;
      const toRect = anchorEl.getBoundingClientRect();
      const dx = toRect.left + toRect.width / 2 - (fromRect.left + fromRect.width / 2 + (opts.xOffset ?? 0));
      const dy = toRect.top + toRect.height / 2 - (fromRect.top + fromRect.height / 2);
      const distance = Math.hypot(dx, dy);
      const riseMs = 480;
      const holdMs = opts.holdMs ?? 1500;
      const travelMs = clamp(Math.round(distance * 1.15), 800, 1800);
      const ms = audioMs(riseMs, holdMs, travelMs);

      const id = window.setTimeout(() => {
        const targetValue = typeof opts.nextValue === 'number' ? opts.nextValue : resolveKpTarget(opts.delta);
        if (Number.isFinite(targetValue)) {
          if (!combined) playKpIncreaseSound();
          triggerKpHeaderEffects(targetValue);
        }
      }, ms);
      extraTimeoutIds.push(id);
    },
    onArrive: () => { /* audio/trigger handled in onBeforeAnimate timeout */ },
    resolveTarget: resolveKpTarget,
    extraTimeoutIds,
  });
};
